import { useCallback, useMemo } from "react";
import { useSearchParams } from "./useUrlSearchParams";

/**
 * One active query param among `filterKeys`. Setting a value clears the others.
 * The first key that has a value is the active one.
 */
export function useExclusiveUrlFilters<K extends string>(filterKeys: readonly K[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const activeFilters = {} as Record<K, string>;
    for (const key of filterKeys) {
      activeFilters[key] = searchParams.get(key) ?? "";
    }
    return activeFilters;
  }, [searchParams, filterKeys]);

  const activeKey = useMemo(() => {
    return filterKeys.find((key) => Boolean(searchParams.get(key))) ?? null;
  }, [searchParams, filterKeys]);

  const setExclusiveFilter = useCallback(
    (keyToSet: K, value: string) => {
      setSearchParams((prevParams) => {
        for (const key of filterKeys) {
          prevParams.delete(key);
        }
        if (value) {
          prevParams.set(keyToSet, value);
        }
        return prevParams;
      });
    },
    [setSearchParams, filterKeys],
  );

  return { filters, activeKey, setExclusiveFilter };
}
